import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import SportLivePeriod from './SportLivePeriod';

import styles from '../../../styles/partials/nhl-live-game-stats-detailed.module.scss';

const propTypes = {
    className: PropTypes.string,
    sportGame: AppPropTypes.sportGame,
    withoutLogo: PropTypes.bool,
};

const defaultProps = {
    className: null,
    sportGame: null,
    withoutLogo: true,
};

function NhlLiveGameStatsDetailed({ className, sportGame, withoutLogo }) {
    const themeClassName = useThemeClassName(styles);
    const {
        periods = [],
        teamAway = null,
        teamHome = null,
        players: initialPlayers = null,
    } = sportGame || {};
    const [players] = useState(initialPlayers);

    const { length: playersCount } = players || [];
    const hasPlayers = playersCount > 0;

    const teams = useMemo(
        () =>
            teamAway !== null && teamHome !== null
                ? [
                      { ...teamAway, isHome: false },
                      { ...teamHome, isHome: true },
                  ]
                : [],
        [teamAway, teamHome],
    );

    const { goals: teamAwayGoals } = teamAway || {};
    const { goals: teamHomeGoals } = teamHome || {};

    const [selectedTeam, setSelectedTeam] = useState(teams[0]);
    const { id: selectedTeamId } = selectedTeam || {};
    const selectedTeamPlayers = useMemo(
        () => (players || []).filter(({ team }) => team === selectedTeamId),
        [players, selectedTeamId],
    );

    const onTeamClick = useCallback((team) => {
        setSelectedTeam(team);
    }, []);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.hideDetails]: !hasPlayers,
                },
                themeClassName,
                className,
            ])}
        >
            <div className={styles.topBanner}>
                <div className={styles.inner}>
                    <div className={styles.main}>
                        <div className={styles.teamsMain}>
                            {teams.map(
                                (
                                    {
                                        abbreviation = null,
                                        logo,
                                        standings = null,
                                        location = null,
                                    },
                                    teamIndex,
                                ) => {
                                    const { url: logoUrl = null } = logo || {};
                                    const { wins = 0, losses = 0, ot = 0 } = standings || {};
                                    return (
                                        <div
                                            key={`team-${teamIndex + 1}`}
                                            className={styles.teamMain}
                                        >
                                            {logoUrl !== null &&
                                            logoUrl.length > 0 &&
                                            !withoutLogo ? (
                                                <img
                                                    className={styles.logo}
                                                    src={logoUrl}
                                                    alt={location}
                                                />
                                            ) : null}
                                            <div className={styles.name}>
                                                {location !== null && location.length > 0
                                                    ? location
                                                    : abbreviation}
                                            </div>
                                            {standings !== null ? (
                                                <div
                                                    className={styles.wlo}
                                                >{`(${wins}-${losses}-${ot})`}</div>
                                            ) : null}
                                        </div>
                                    );
                                },
                            )}
                        </div>
                        <div className={styles.liveScore}>
                            <span className={styles.liveGoals}>{teamAwayGoals}</span>
                            <span className={styles.separator}>-</span>
                            <span className={styles.liveGoals}>{teamHomeGoals}</span>
                        </div>
                    </div>
                    <div className={styles.livePeriodContainer}>
                        <SportLivePeriod
                            className={styles.livePeriod}
                            sportGame={sportGame}
                            periods={periods}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.spacer} />
            <div className={styles.inner}>
                <table className={styles.teamGoalsPeriods}>
                    <thead>
                        <tr>
                            <td>
                                <FormattedMessage defaultMessage="Teams" description="Label" />
                            </td>
                            {(periods || []).map(({ number }, periodIndex) => (
                                <td key={`period-${periodIndex + 1}`}>{number}</td>
                            ))}
                            <td>
                                <FormattedMessage
                                    defaultMessage="T"
                                    description="NHL stats - Total"
                                />
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        {teams.map(
                            ({ location = null, abbreviation = null, isHome }, teamIndex) => (
                                <tr key={`team-${teamIndex + 1}`} className={styles.teamName}>
                                    <td>
                                        {location !== null && location.length > 0
                                            ? location
                                            : abbreviation}
                                    </td>
                                    {(periods || []).map(
                                        ({ teamHome: th, teamAway: ta }, periodIndex) => {
                                            const { goals } = (isHome ? th : ta) || {};
                                            return (
                                                <td key={`period-${periodIndex + 1}`}>{goals}</td>
                                            );
                                        },
                                    )}
                                    <td>{isHome ? teamHomeGoals : teamAwayGoals}</td>
                                </tr>
                            ),
                        )}
                    </tbody>
                </table>
                <div className={styles.teamDetailsSelector}>
                    {teams.map((team, teamIndex) => {
                        const { location, abbreviation, id } = team || {};
                        return (
                            <Button
                                onClick={() => {
                                    onTeamClick(team);
                                }}
                                key={`team-${teamIndex + 1}`}
                                className={classNames([
                                    styles.teamButton,
                                    { [styles.active]: id === selectedTeamId },
                                ])}
                            >
                                {location !== null && location.length > 0 ? location : abbreviation}
                            </Button>
                        );
                    })}
                </div>
                <table className={styles.selectedTeamPlayers}>
                    <thead>
                        <tr>
                            <td>
                                <FormattedMessage defaultMessage="Players" description="Label" />
                            </td>
                            <td>
                                <FormattedMessage
                                    defaultMessage="G"
                                    description="NHL stats - Goals"
                                />
                            </td>
                            <td>
                                <FormattedMessage
                                    defaultMessage="A"
                                    description="NHL stats - Assists"
                                />
                            </td>
                            <td>
                                <FormattedMessage
                                    defaultMessage="PTS"
                                    description="NHL stats - Points"
                                />
                            </td>
                            <td>
                                <FormattedMessage
                                    defaultMessage="SOG"
                                    description="NHL stats - Shots on goal"
                                />
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        {selectedTeamPlayers.map(
                            ({ name, goals, assists, points, shots }, playerIndex) => (
                                <tr key={`player-${playerIndex + 1}`}>
                                    <td>{name}</td>
                                    <td>{goals}</td>
                                    <td>{assists}</td>
                                    <td>{points}</td>
                                    <td>{shots}</td>
                                </tr>
                            ),
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

NhlLiveGameStatsDetailed.propTypes = propTypes;
NhlLiveGameStatsDetailed.defaultProps = defaultProps;

export default NhlLiveGameStatsDetailed;
